import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Upload, Download, ArrowUpRight, ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
  Loader2, FolderOpen, AlertCircle, CheckCircle2, Clock, XCircle, Files, ShieldAlert, Trash2, PenOff
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { PermissionGuard } from '../../../hooks/usePermissions';
import { useRepositoryContracts, useSupplierCoverage, useSuppliers, useActiveContractGroups } from '../../../hooks/queries';
import type {
  RepositoryContract,
  RepositoryContractFilters,
  ContractCategory,
  ExpiryStatus
} from '../../../services/api/contractService';
import { CONTRACT_CATEGORIES, categoryLabel } from '../../../utils/contractCategories';
import { downloadContractFile } from '../../../utils/downloadContractFile';
import { AddToRepositoryModal } from './AddToRepositoryModal';
import { BatchImportModal } from './BatchImportModal';
import { SupplierCoverage } from './SupplierCoverage';
import { DeleteRepositoryContractDialog } from './DeleteRepositoryContractDialog';
import { SignatureBadge } from './signature';

const PAGE_SIZE = 25;

type SortField = NonNullable<RepositoryContractFilters['sortBy']>;
type RepositoryView = 'contracts' | 'coverage';

const STATUS_META: Record<
  ExpiryStatus,
  { label: string; badge: string; tile: string; tileActive: string; icon: React.ElementType; iconClass: string }
> = {
  active: {
    label: 'Active',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
    tile: 'hover:border-emerald-300 dark:hover:border-emerald-700',
    tileActive: 'border-emerald-500 ring-2 ring-emerald-500/20 dark:border-emerald-500',
    icon: CheckCircle2,
    iconClass: 'text-emerald-600 dark:text-emerald-400'
  },
  expiring_soon: {
    label: 'Expiring soon',
    badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    tile: 'hover:border-amber-300 dark:hover:border-amber-700',
    tileActive: 'border-amber-500 ring-2 ring-amber-500/20 dark:border-amber-500',
    icon: Clock,
    iconClass: 'text-amber-600 dark:text-amber-400'
  },
  expired: {
    label: 'Expired',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
    tile: 'hover:border-rose-300 dark:hover:border-rose-700',
    tileActive: 'border-rose-500 ring-2 ring-rose-500/20 dark:border-rose-500',
    icon: XCircle,
    iconClass: 'text-rose-600 dark:text-rose-400'
  }
};

const inputClass =
  'w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors';

// Contract dates are date-only values stored at midnight UTC; show them as that calendar day.
const formatDate = (value?: string): string =>
  value
    ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '—';

const describeExpiry = (days: number): string => {
  if (days < 0) return days === -1 ? 'Ended yesterday' : `Ended ${-days} days ago`;
  if (days === 0) return 'Ends today';
  if (days === 1) return 'Ends tomorrow';
  if (days < 60) return `Ends in ${days} days`;
  const months = Math.round(days / 30.44);
  return months < 24 ? `Ends in ${months} months` : `Ends in ${Math.floor(months / 12)} years`;
};

const StatusBadge: React.FC<{ status: ExpiryStatus }> = ({ status }) => (
  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${STATUS_META[status].badge}`}>
    {STATUS_META[status].label}
  </span>
);

interface ContractRepositoryProps {
  /** Called after a contract is added here, so views holding their own contract list can reload. */
  onContractsChanged?: () => void;
}

/**
 * Contract repository: every signed contract, with its expiry status worked
 * out from its end date. A contract lands here once a signed document is
 * uploaded to it.
 */
export const ContractRepository: React.FC<ContractRepositoryProps> = ({ onContractsChanged }) => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [category, setCategory] = useState<ContractCategory | ''>('');
  const [expiryStatus, setExpiryStatus] = useState<ExpiryStatus | ''>('');
  const [endFrom, setEndFrom] = useState('');
  const [endTo, setEndTo] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('endDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [view, setView] = useState<RepositoryView>('contracts');
  const [onlyFlaggedSignatures, setOnlyFlaggedSignatures] = useState(false);
  const [deleting, setDeleting] = useState<RepositoryContract | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Debounce typing so every keystroke isn't a request.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any change to what's being looked at starts again from page 1.
  useEffect(() => {
    setPage(1);
  }, [search, supplierId, groupId, category, expiryStatus, endFrom, endTo, sortBy, sortOrder, onlyFlaggedSignatures]);

  const { data, isLoading, isFetching, isError, error } = useRepositoryContracts({
    search: search || undefined,
    supplierId: supplierId || undefined,
    groupId: groupId || undefined,
    category: category || undefined,
    expiryStatus: expiryStatus || undefined,
    endFrom: endFrom || undefined,
    endTo: endTo || undefined,
    signature: onlyFlaggedSignatures ? 'flagged' : undefined,
    sortBy,
    sortOrder,
    page,
    limit: PAGE_SIZE
  });
  // The highlight panel always shows the soonest expiries, whatever the filters.
  const { data: expiringData } = useRepositoryContracts({
    expiryStatus: 'expiring_soon',
    sortBy: 'endDate',
    sortOrder: 'asc',
    limit: 5
  });
  // Just the counts, for the tab badge and the banner.
  const { data: coverageData } = useSupplierCoverage({ coverage: 'missing' });
  const missingCount = coverageData ? coverageData.counts.none + coverageData.counts.expired : 0;
  const { data: suppliers = [] } = useSuppliers();
  const { data: groups = [] } = useActiveContractGroups();

  const contracts = data?.contracts ?? [];
  const pagination = data?.pagination;
  const counts = data?.counts;
  const months = data?.expiringSoonMonths ?? 6;
  const expiringSoon = expiringData?.contracts ?? [];
  const expiringSoonTotal = expiringData?.pagination.total ?? 0;
  const flaggedSignatures = data?.flaggedSignatures ?? 0;
  const hasFilters = Boolean(
    search || supplierId || groupId || category || expiryStatus || endFrom || endTo || onlyFlaggedSignatures
  );

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setSupplierId('');
    setGroupId('');
    setCategory('');
    setExpiryStatus('');
    setEndFrom('');
    setEndTo('');
    setOnlyFlaggedSignatures(false);
  };

  const showAllExpiringSoon = () => {
    resetFilters();
    setExpiryStatus('expiring_soon');
    setSortBy('endDate');
    setSortOrder('asc');
  };

  const showSupplierContracts = (id: string) => {
    resetFilters();
    setSupplierId(id);
    setView('contracts');
  };

  const toggleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleDownload = async (contract: RepositoryContract) => {
    if (!token) return;
    const file = contract.latestSignedFile;
    setDownloadError(null);
    setDownloadingId(file._id);
    try {
      await downloadContractFile(token, file._id, file.originalName);
    } catch (err) {
      console.error('File download failed:', err);
      setDownloadError(err instanceof Error && err.message ? err.message : `Couldn't download ${file.originalName}. Try again.`);
    } finally {
      setDownloadingId(null);
    }
  };

  const openReview = (contract: RepositoryContract) => navigate(`/contracts/${contract._id}/overview`);

  const SortHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({
    field,
    children,
    className = ''
  }) => {
    const active = sortBy === field;
    return (
      <th
        scope="col"
        aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${className}`}
      >
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          {children}
          {active && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
        </button>
      </th>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contract Repository</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Every signed contract, with its status worked out from the contract dates.
          </p>
        </div>
        <PermissionGuard permission="contracts.files.upload">
          <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setShowBatchModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <Files className="w-4 h-4" />
            Import in bulk
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 dark:text-gray-900 rounded-lg shadow-sm hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <Upload className="w-4 h-4" />
            Add signed contract
          </button>
          </div>
        </PermissionGuard>
      </div>

      <div role="tablist" aria-label="Repository view" className="flex gap-6 border-b border-gray-200 dark:border-dark-border">
        {(
          [
            { key: 'contracts', label: 'Contracts' },
            { key: 'coverage', label: 'Supplier coverage', count: missingCount }
          ] as { key: RepositoryView; label: string; count?: number }[]
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={view === tab.key}
            onClick={() => setView(tab.key)}
            className={`-mb-px pb-2.5 text-sm font-medium border-b-2 transition-colors ${
              view === tab.key
                ? 'border-primary-600 dark:border-accent-400 text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
            {tab.count ? (
              <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 tabular-nums">
                {tab.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {view === 'coverage' && (
        <SupplierCoverage onShowContracts={showSupplierContracts} onAddContract={() => setShowAddModal(true)} />
      )}

      {view === 'contracts' && (
        <>
          {missingCount > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-rose-50/70 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-900/50 rounded-xl">
              <p className="flex items-center gap-2 text-sm text-rose-900 dark:text-rose-200">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" aria-hidden />
                {missingCount} active supplier{missingCount === 1 ? ' has' : 's have'} no valid signed contract.
              </p>
              <button
                type="button"
                onClick={() => setView('coverage')}
                className="self-start sm:self-auto text-sm font-medium text-rose-900 dark:text-rose-200 hover:underline"
              >
                Review suppliers
              </button>
            </div>
          )}

          {/* Status tiles double as a status filter */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(Object.keys(STATUS_META) as ExpiryStatus[]).map((status) => {
              const meta = STATUS_META[status];
              const Icon = meta.icon;
              const selected = expiryStatus === status;
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setExpiryStatus(selected ? '' : status)}
                  className={`flex items-center gap-4 p-4 text-left bg-white dark:bg-dark-surface rounded-xl border shadow-sm transition-all ${
                    selected ? meta.tileActive : `border-gray-100 dark:border-dark-border ${meta.tile}`
                  }`}
                >
                  <Icon className={`w-8 h-8 flex-shrink-0 ${meta.iconClass}`} aria-hidden />
                  <div>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">
                      {counts ? counts[status] : '–'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {meta.label}
                      {status === 'expiring_soon' && <span className="text-gray-400 dark:text-gray-500"> · next {months} months</span>}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Soonest expiries, regardless of filters */}
          {expiringSoon.length > 0 && (
            <section
              aria-labelledby="expiring-soon-heading"
              className="bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/50 rounded-xl p-4 sm:p-5"
            >
              <div className="flex items-center justify-between gap-4 mb-3">
                <h3 id="expiring-soon-heading" className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                  <Clock className="w-4 h-4" aria-hidden />
                  Expiring in the next {months} months
                </h3>
                {expiringSoonTotal > expiringSoon.length && (
                  <button
                    type="button"
                    onClick={showAllExpiringSoon}
                    className="text-sm font-medium text-amber-900 dark:text-amber-200 hover:underline"
                  >
                    View all {expiringSoonTotal}
                  </button>
                )}
              </div>
              <ul className="divide-y divide-amber-200/70 dark:divide-amber-900/50">
                {expiringSoon.map((contract) => (
                  <li key={contract._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4 py-2">
                    <button
                      type="button"
                      onClick={() => openReview(contract)}
                      className="min-w-0 text-left group"
                    >
                      <span className="block text-sm font-medium text-gray-900 dark:text-white truncate group-hover:underline">
                        {contract.contractTitle}
                      </span>
                      <span className="block text-xs text-gray-600 dark:text-gray-400 truncate">
                        {[contract.supplier?.companyName, contract.group?.name].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                    <span className="text-sm text-amber-900 dark:text-amber-200 whitespace-nowrap tabular-nums">
                      {formatDate(contract.endDate)} · {describeExpiry(contract.daysUntilExpiry)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Filters */}
          <div className="bg-white dark:bg-dark-surface p-4 sm:p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="repo-search" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  <input
                    id="repo-search"
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Contract name or number, supplier, group…"
                    className={`${inputClass} pl-9 placeholder-gray-500 dark:placeholder-gray-400`}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="repo-supplier" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <select id="repo-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputClass}>
                  <option value="">All suppliers</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="repo-group" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group
                </label>
                <select id="repo-group" value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputClass}>
                  <option value="">All groups</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="repo-category" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  id="repo-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ContractCategory | '')}
                  className={inputClass}
                >
                  <option value="">All categories</option>
                  {CONTRACT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="repo-status" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id="repo-status"
                  value={expiryStatus}
                  onChange={(e) => setExpiryStatus(e.target.value as ExpiryStatus | '')}
                  className={inputClass}
                >
                  <option value="">All statuses</option>
                  {(Object.keys(STATUS_META) as ExpiryStatus[]).map((s) => (
                    <option key={s} value={s}>{STATUS_META[s].label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="repo-end-from" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ends on or after
                </label>
                <input
                  id="repo-end-from"
                  type="date"
                  value={endFrom}
                  max={endTo || undefined}
                  onChange={(e) => setEndFrom(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="repo-end-to" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ends on or before
                </label>
                <input
                  id="repo-end-to"
                  type="date"
                  value={endTo}
                  min={endFrom || undefined}
                  onChange={(e) => setEndTo(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            {hasFilters && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {(flaggedSignatures > 0 || onlyFlaggedSignatures) && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
              <span className="flex items-center gap-2">
                <PenOff className="w-4 h-4 flex-shrink-0" aria-hidden />
                {flaggedSignatures} contract{flaggedSignatures === 1 ? '' : 's'} may not have a signed copy on file.
              </span>
              <button
                type="button"
                aria-pressed={onlyFlaggedSignatures}
                onClick={() => setOnlyFlaggedSignatures(!onlyFlaggedSignatures)}
                className="self-start sm:self-auto font-medium hover:underline"
              >
                {onlyFlaggedSignatures ? 'Show all contracts' : 'Show them'}
              </button>
            </div>
          )}

          {downloadError && (
            <div role="alert" className="flex items-center gap-2 p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {downloadError}
            </div>
          )}

          {/* Results */}
          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading contracts…
              </div>
            ) : isError ? (
              <div role="alert" className="flex flex-col items-center gap-2 py-16 text-center">
                <AlertCircle className="w-8 h-8 text-rose-500" />
                <p className="text-sm font-medium text-gray-900 dark:text-white">Couldn't load the repository</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{error?.message}</p>
              </div>
            ) : contracts.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 px-4 text-center">
                <FolderOpen className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {hasFilters ? 'No contracts match these filters' : 'No signed contracts yet'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  {hasFilters
                    ? 'Try a different search or clear the filters.'
                    : 'Upload a signed contract and it will appear here with its status.'}
                </p>
              </div>
            ) : (
              // Fixed layout so the table always fits its card: columns drop out as the
              // screen narrows and their details move into the contract's second line.
              <div className={`transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
                <table className="w-full table-fixed divide-y divide-gray-100 dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                    <tr>
                      <SortHeader field="contractTitle">Contract</SortHeader>
                      <SortHeader field="supplierName" className="hidden md:table-cell w-[24%]">Supplier / Group</SortHeader>
                      <th scope="col" className="hidden xl:table-cell w-32 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Category
                      </th>
                      <SortHeader field="endDate" className="hidden lg:table-cell w-56">Period</SortHeader>
                      <th scope="col" className="w-36 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th scope="col" className="w-36 px-3 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                    {contracts.map((contract) => (
                      <tr key={contract._id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={contract.contractTitle}>
                            {contract.contractTitle}
                          </p>
                          {/* Whatever the hidden columns would have shown. */}
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            <span className="md:hidden">
                              {contract.supplier?.companyName}
                              <span className="lg:hidden"> · </span>
                            </span>
                            <span className="lg:hidden tabular-nums">Ends {formatDate(contract.endDate)}</span>
                            <span className="hidden lg:inline">{contract.contractNumber}</span>
                          </p>
                        </td>
                        <td className="hidden md:table-cell px-4 py-3">
                          <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={contract.supplier?.companyName}>
                            {contract.supplier?.companyName || '—'}
                          </p>
                          {contract.group && (
                            <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: contract.group.color || '#6b7280' }}
                                aria-hidden
                              />
                              <span className="truncate">{contract.group.name}</span>
                            </p>
                          )}
                        </td>
                        <td className="hidden xl:table-cell px-4 py-3 text-sm text-gray-700 dark:text-gray-300 truncate">
                          {categoryLabel(contract.category) || <span className="text-gray-400 dark:text-gray-500">Not set</span>}
                        </td>
                        <td className="hidden lg:table-cell px-4 py-3">
                          <p className="text-sm text-gray-700 dark:text-gray-300 tabular-nums truncate">
                            {formatDate(contract.startDate)} – {formatDate(contract.endDate)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{describeExpiry(contract.daysUntilExpiry)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start gap-1">
                            <StatusBadge status={contract.expiryStatus} />
                            <SignatureBadge
                              status={contract.latestSignedFile.signatureCheck?.status}
                              notes={contract.latestSignedFile.signatureCheck?.notes}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleDownload(contract)}
                              disabled={downloadingId === contract.latestSignedFile._id}
                              aria-label={`Download signed contract ${contract.latestSignedFile.originalName}`}
                              title={`Download ${contract.latestSignedFile.originalName}`}
                              className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors disabled:opacity-50"
                            >
                              {downloadingId === contract.latestSignedFile._id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => openReview(contract)}
                              aria-label={`Open ${contract.contractTitle}`}
                              title="Open contract"
                              className="inline-flex items-center gap-0.5 px-2 py-1.5 text-xs font-medium text-primary-700 dark:text-accent-400 rounded-lg hover:bg-primary-50 dark:hover:bg-dark-surface-raised transition-colors"
                            >
                              Open
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                            <PermissionGuard permissions={['contracts.delete', 'contracts.files.delete']} requireAll={false}>
                              <button
                                type="button"
                                onClick={() => setDeleting(contract)}
                                aria-label={`Delete ${contract.contractTitle}`}
                                title="Delete"
                                className="p-2 text-gray-500 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination && pagination.total > 0 && (
              <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-100 dark:border-dark-border">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.hasPrevPage}
                    aria-label="Previous page"
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasNextPage}
                    aria-label="Next page"
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </>
      )}

      <DeleteRepositoryContractDialog
        contract={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={onContractsChanged}
      />
      <AddToRepositoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdded={onContractsChanged}
      />
      <BatchImportModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onAdded={onContractsChanged}
      />
    </div>
  );
};

export default ContractRepository;
